import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Img from "@insite/mobius/Img";
import LazyImage from "@insite/mobius/LazyImage";
import { LinkProps } from "@insite/mobius/Link";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowUpIcon from "@tCentro/Icons/ArrowUpIcon";
import CircleCrossIcon from "@tCentro/Icons/CircleCrossIcon";
import CircleInfoIcon from "@tCentro/Icons/CircleInfoIcon";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import { css } from "styled-components";

interface OfferingModel {
    fields: {
        name: string;
        image: string;
        description: string;
        linkTitle: string;
        destination: LinkFieldValue;
        linkTitle2: string;
        destination2: LinkFieldValue;
    };
}

const enum fields {
    offerings = "offerings",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.offerings]: OfferingModel[];
    };
    extendedStyles?: OfferingStyles;
}

export interface OfferingStyles {
    link?: LinkProps;
    linksWrapper?: InjectableCss;
    cardWrapper?: InjectableCss;
    descriptionText?: TypographyProps;
    nameLogoWrapper: InjectableCss;
    logoWrapper: InjectableCss;
    companyName: InjectableCss;
    container: InjectableCss;
    gridWrapper?: GridContainerProps;
    gridItem?: GridItemProps;
    card2: InjectableCss;
    card1: InjectableCss;
    mobileIconWrapper: InjectableCss;
    button: InjectableCss;
    seperator: InjectableCss;
    linkUrlWraaper: InjectableCss;
}

export const offeringStyles: OfferingStyles = {
    gridWrapper: {
        css: css`
            flex-wrap: wrap;
        `,
    },
    gridItem: {
        width: [12, 12, 6, 3, 3],
        css: css`
            align-items: center;
            padding: 12px !important;
            flex-direction: column;
            @media (min-width: 992px) and (max-width: 1210px) {
                max-width: 33.33% !important;
                flex-basis: 33.33% !important;
            }
        `,
    },
    container: {
        css: css`
            cursor: pointer;
            width: 100%;
            &:hover {
                .card1 {
                    display: none;
                }
                .card2 {
                    display: block;
                    box-shadow: 0px 8px 16px rgba(38, 37, 37, 0.24);
                }
            }
        `,
    },
    card1: {
        css: css`
            display: block;
        `,
    },
    card2: {
        css: css`
            display: none;
        `,
    },
    nameLogoWrapper: {
        css: css`
            background-color: ${COLORS.background.grayShade};
            padding: 32px;
            margin-bottom: 30px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 48px 0px 0px;
                        margin-bottom: 0px;
                    `,
                    css`
                        padding: 48px 0px 0px;
                        margin-bottom: 0px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    logoWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    companyName: {
        css: css`
            font-size: ${TYPOGRAPHY.size.large} !important;
            margin-top: 48px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        text-align: center;
                    `,
                    css`
                        text-align: center;
                    `,
                    css`
                        text-align: center;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    link: {
        css: css`
            display: flex;
            position: relative;
            color: ${COLORS.link.base};
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
            text-transform: capitalize;
        `,
    },
    linksWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            margin-top: 16px;

            & > div:first-child {
                padding-right: 8px;
            }

            & > div:not(:first-child) {
                border-left: 2px solid ${COLORS.border.light};
                padding: 0 8px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0px 32px;
                    `,
                    css`
                        padding: 0px 32px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    linkUrlWraaper: {
        css: css`
            display: flex;
        `,
    },
    cardWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            background-color: ${COLORS.background.grayShade};
            padding: 32px;
            margin-bottom: 30px;
            cursor: pointer;
            width: 100%;
            height: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0px;
                        margin-bottom: 0px;
                    `,
                    css`
                        padding: 0px;
                        margin-bottom: 0px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    descriptionText: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: ${getProp("theme.typography.p.color")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 32px 32px 0px; ;
                    `,
                    css`
                        padding: 32px 32px 0px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    mobileIconWrapper: {
        css: css`
            display: none;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: flex;
                            justify-content: flex-end;
                            padding-right: 12px;
                            padding-bottom: 12px;
                        `,
                        css`
                            display: flex;
                            justify-content: flex-end;
                            padding-right: 12px;
                            padding-bottom: 12px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    button: {
        css: css`
            display: flex;
            justify-content: center;
            flex-basis: 100%;
            margin-top: 22px;
            margin-bottom: 104px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 44px;
                        margin-bottom: 56px;
                    `,
                    css`
                        margin-top: 44px;
                        margin-bottom: 56px;
                    `,
                    css`
                        margin-top: 44px;
                        margin-bottom: 78px;
                    `,
                    null,
                    null,
                ])}
            button {
                background: none !important;
                color: ${COLORS.secondary.base} !important;
                padding: 12px 20px;
                height: 48px;
                width: 172px;
                font-size: ${TYPOGRAPHY.size.large};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: ${TYPOGRAPHY.lineHeight.base};
            }
            span {
                height: 18px;
                width: 11.25px;
                margin-left: 14px;
            }
            svg {
                height: 18px;
                width: 11.25px;
            }
        `,
    },
    seperator: {
        css: css`
            display: inline-block;
            height: 10px;
            width: 1px;
            background: ${getColor("common.borderLight")};
            margin: 5px 5px;
        `,
    },
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        pageLink: ownProps.fields.offerings.map(card => getLink(state, card.fields.destination)),
        hoverPageLink: ownProps.fields.offerings.map(hoverCard => getLink(state, hoverCard.fields.destination2)),
    };
};

const mapDispatchToProps = {};
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const SectionTemplateTextLinks: React.FC<Props> = ({ fields, pageLink, hoverPageLink, extendedStyles }) => {
    const [styles] = useState(() => mergeToNew(offeringStyles, extendedStyles));

    return (
        <GridContainer {...styles.gridWrapper}>
            {fields.offerings &&
                fields.offerings.length &&
                fields.offerings.map((offering, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <GridItem {...styles.gridItem} key={index}>
                        <StyledWrapper {...styles.container}>
                            <StyledWrapper className="card1" {...styles.card1}>
                                <StyledWrapper {...styles.nameLogoWrapper}>
                                    <StyledWrapper {...styles.logoWrapper}>
                                        <Img src={offering.fields.image} altText={offering.fields.name} loading="lazy" />
                                    </StyledWrapper>
                                    <Typography variant="h5" {...styles.companyName}>
                                        {translate("{0}", offering.fields.name)}
                                    </Typography>
                                    <StyledWrapper
                                        data-test-selector="mobile-hover-icons"
                                        {...styles.mobileIconWrapper}
                                    >
                                        <Icon src={CircleInfoIcon} className="defaultMobileIcon" />
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper className="card2" {...styles.card2}>
                                <StyledWrapper {...styles.cardWrapper}>
                                    <Typography {...styles.descriptionText}>{offering.fields.description}</Typography>
                                    <StyledWrapper {...styles.linksWrapper}>
                                        <StyledWrapper {...styles.linkUrlWraaper}>
                                            <Clickable href={pageLink[index]?.url} {...styles.link} target="_blank">
                                                {translate("{0}", offering.fields.linkTitle)}
                                            </Clickable>
                                            <Typography {...styles.seperator}></Typography>
                                            <Clickable
                                                href={hoverPageLink[index]?.url}
                                                {...styles.link}
                                                target="_blank"
                                            >
                                                {translate("{0}", offering.fields.linkTitle2)}
                                            </Clickable>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.mobileIconWrapper}>
                                        <Icon src={CircleCrossIcon} className="hoverMobileIcon" />
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>
                ))}
            <StyledWrapper {...styles.button}>
                <Button>
                    {translate("Show More")} <Icon src={ArrowUpIcon} />
                </Button>
            </StyledWrapper>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SectionTemplateTextLinks),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.offerings,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "description",
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                    },
                    {
                        name: "linkTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "linkTitle2",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "destination2",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
